/**
 * SocialIcons — atomic SVG marks for SSO providers and third-party brands.
 *
 * lucide-react v1 dropped its brand/social icons (Youtube, Linkedin, …), so
 * brand marks live here rather than being imported from lucide.
 *
 * Source: features/auth-core.yml components.shared (SocialIcons);
 *         docs/implementation-blueprint.md §8.1-§8.2 (atom contract:
 *         ≤120 LoC, forwardRef + displayName, named export).
 *
 * Each icon forwards a ref to its `<svg>` element so consumers can attach
 * tooltips or animations. Colors are `currentColor`, so the containing
 * `<Button>`'s text color cascades.
 *
 * GoogleIcon is the one SSO mark (Google is the only social sign-in provider).
 *
 * MIGRATION-STATUS: pure-SVG atoms — no Tailwind utility-class strings to
 * migrate. Each icon renders inline SVG markup with `fill="currentColor"`.
 * Consumers theme via the surrounding text color (`currentColor` cascade) and
 * may override fill / className / size via the spread
 * `React.ComponentProps<"svg">` surface. No co-located .css file is needed; the
 * atom is conformant with `forwardRef + displayName + named export`
 * requirements per docs/implementation-blueprint.md §8.
 */

import * as React from "react";
import { memo } from "react";

export const GoogleIcon = memo(
    React.forwardRef<SVGSVGElement, React.ComponentProps<"svg">>(function GoogleIcon(props, ref) {
        return (
            <svg ref={ref} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-component="GoogleIcon" data-location="src/components/ui/atoms/SocialIcons.tsx" {...props}>
                <g>
                    <path d="M12.479,14.265v-3.279h11.049c0.108,0.571,0.164,1.247,0.164,1.979c0,2.46-0.672,5.502-2.84,7.669   C18.744,22.829,16.051,24,12.483,24C5.869,24,0.308,18.613,0.308,12S5.869,0,12.483,0c3.659,0,6.265,1.436,8.223,3.307L18.392,5.62   c-1.404-1.317-3.307-2.341-5.913-2.341C7.65,3.279,3.873,7.171,3.873,12s3.777,8.721,8.606,8.721c3.132,0,4.916-1.258,6.059-2.401   c0.927-0.927,1.537-2.251,1.777-4.059L12.479,14.265z" />
                </g>
            </svg>
        );
    })
);
GoogleIcon.displayName = "GoogleIcon";

const AppleIcon = memo(
    React.forwardRef<SVGSVGElement, React.ComponentProps<"svg">>(function AppleIcon({ fill = "currentColor", ...props }, ref) {
        return (
            <svg ref={ref} fill={fill} viewBox="0 0 24 24" data-component="AppleIcon" data-location="src/components/ui/atoms/SocialIcons.tsx" {...props}>
                <g id="_Group_2">
                    <g id="_Group_3">
                        <path d="M18.546,12.763c0.024-1.87,1.004-3.597,2.597-4.576c-1.009-1.442-2.64-2.323-4.399-2.378    c-1.851-0.194-3.645,1.107-4.588,1.107c-0.961,0-2.413-1.088-3.977-1.056C6.122,5.927,4.25,7.068,3.249,8.867    c-2.131,3.69-0.542,9.114,1.5,12.097c1.022,1.461,2.215,3.092,3.778,3.035c1.529-0.063,2.1-0.975,3.945-0.975    c1.828,0,2.364,0.975,3.958,0.938c1.64-0.027,2.674-1.467,3.66-2.942c0.734-1.041,1.299-2.191,1.673-3.408    C19.815,16.788,18.548,14.879,18.546,12.763z" id="_Path_" />
                        <path d="M15.535,3.847C16.429,2.773,16.87,1.393,16.763,0c-1.366,0.144-2.629,0.797-3.535,1.829    c-0.895,1.019-1.349,2.351-1.261,3.705C13.352,5.548,14.667,4.926,15.535,3.847z" id="_Path_2" />
                    </g>
                </g>
            </svg>
        );
    })
);
AppleIcon.displayName = "AppleIcon";

export const YoutubeIcon = memo(
    React.forwardRef<SVGSVGElement, React.ComponentProps<"svg">>(function YoutubeIcon(props, ref) {
        return (
            <svg ref={ref} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-component="YoutubeIcon" data-location="src/components/ui/atoms/SocialIcons.tsx" {...props}>
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
        );
    })
);
YoutubeIcon.displayName = "YoutubeIcon";

export const LinkedinIcon = memo(
    React.forwardRef<SVGSVGElement, React.ComponentProps<"svg">>(function LinkedinIcon(props, ref) {
        return (
            <svg ref={ref} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-component="LinkedinIcon" data-location="src/components/ui/atoms/SocialIcons.tsx" {...props}>
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
            </svg>
        );
    })
);
LinkedinIcon.displayName = "LinkedinIcon";
